import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import customerService from "../../services/customerService";
import { Button, Badge, Form, Spinner, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import "bootstrap/dist/css/bootstrap.min.css";
import InfoPill from "../Common/InfoPill";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";

const CustomersList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [customerToUpdate, setCustomerToUpdate] = useState(null);
  const [newStatus, setNewStatus] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await customerService.list();
      let customers = res.customers || [];

      // Apply status filter
      if (statusFilter !== "") {
        const filterActive = statusFilter === "true";
        customers = customers.filter(c => c.is_active === filterActive);
      }

      setRows(customers);
    } catch (e) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter]);

  const handleToggleStatus = (customer) => {
    if (!customer) return;
    setCustomerToUpdate(customer);
    setNewStatus(!customer.is_active);
    setShowStatusModal(true);
  };

  const confirmToggleStatus = async () => {
    if (!customerToUpdate) return;

    setUpdatingStatus((prev) => ({ ...prev, [customerToUpdate.id]: true }));

    try {
      await customerService.toggleStatus(customerToUpdate.id, newStatus);
      toast.success(`Customer ${newStatus ? "activated" : "deactivated"} successfully`);
      // Update the customer in the list
      setRows((prev) =>
        prev.map((c) =>
          c.id === customerToUpdate.id ? { ...c, is_active: newStatus } : c
        )
      );
      setShowStatusModal(false);
      setCustomerToUpdate(null);
      setNewStatus(null);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to update customer status");
    } finally {
      setUpdatingStatus((prev) => ({ ...prev, [customerToUpdate.id]: false }));
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge bg="success">Active</Badge>
    ) : (
      <Badge bg="secondary">Inactive</Badge>
    );
  };

  // Helper function to convert date to DD-MM-YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return "";

    // Check if already in DD-MM-YYYY format
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      return dateString;
    }

    // Check if in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split("-");
      return `${day}-${month}-${year}`;
    }

    // Check if in DD/MM/YYYY format (backward compatibility)
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      return dateString.replace(/\//g, "-");
    }

    // Try to parse as Date and convert (avoid timezone issues)
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      // Use local date components to avoid timezone shift
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }

    return dateString;
  };

  const headers = [
    {
      prop: "name",
      title: "Name",
      isSortable: true,
      isFilterable: true,
      cell: (row) => {
        const fullName = `${row.first_name || ""} ${row.last_name || ""}`.trim() || "Unknown";
        return (
          <Link
            to={`/admin/customers/details/${row.id}`}
            className="text-decoration-none fw-bold text-primary"
            style={{ cursor: "pointer" }}
          >
            {fullName}
          </Link>
        );
      },
    },
    {
      prop: "email",
      title: "Email",
      isSortable: true,
      isFilterable: true,
    },
    {
      prop: "phone",
      title: "Phone",
      isSortable: true,
      isFilterable: true,
    },
    // {
    //   prop: "status",
    //   title: "Status",
    //   isSortable: true,
    //   cell: (row) => getStatusBadge(row.is_active),
    // },
    {
      prop: "created_at",
      title: "Created",
      isSortable: true,
      isFilterable: true,
      cell: (row) => formatDate(row.created_at),
    },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant={row.is_active ? "success" : "secondary"}
            onClick={() => handleToggleStatus(row)}
            disabled={loading || updatingStatus[row.id]}
            title={row.is_active ? "Customer is Active - Click to deactivate" : "Customer is Inactive - Click to activate"}
          >
            {updatingStatus[row.id] ? (
              <>
                <Spinner size="sm" animation="border" className="me-1" />
                Updating...
              </>
            ) : row.is_active ? (
              <>
                <FaCheckCircle className="me-1" />
                Active
              </>
            ) : (
              <>
                <FaTimesCircle className="me-1" />
                Inactive
              </>
            )}
          </Button>
        </div>
      ),
    },
  ];

  const labels = {
    beforeSelect: " ",
  };

  if (loading && rows.length === 0) {
    return <div className="text-center mt-4">Loading customers...</div>;
  }

  return (
    <div className="page-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Customers
        </Link>
      </div>

      <DatatableWrapper
        body={Array.isArray(rows) ? rows : []}
        headers={headers}
        paginationOptionsProps={{
          initialState: { rowsPerPage: 5, options: [5, 10, 15, 20] },
        }}
      >
        <div className="d-flex align-items-left mb-2">
          <div className="d-flex align-items-center gap-2 flex-grow-1">
            {/* Search Filter */}
            <div style={{ maxWidth: "300px" }}>
              <Filter
                placeholder="Search customers..."
                className="w-100 btn-success"
              />
            </div>

            {/* Status Filter */}
            <div style={{ maxWidth: "150px" }}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Form.Select>
            </div>
            <PaginationOptions labels={labels} />
            <InfoPill
              left="Total Customers"
              right={rows?.length || 0}
            />
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive shadow-sm">
          <table className="table table-striped table-bordered align-middle">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        {/* Pagination */}
        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",
          }} />
        </div>
      </DatatableWrapper>

      {/* Status Change Confirmation Modal */}
      <Modal
        show={showStatusModal}
        onHide={() => {
          setShowStatusModal(false);
          setCustomerToUpdate(null);
          setNewStatus(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {newStatus ? "Activate Customer" : "Deactivate Customer"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to {newStatus ? "activate" : "deactivate"} customer{" "}
          <strong>
            {customerToUpdate?.first_name || ""} {customerToUpdate?.last_name || ""}
          </strong>
          ?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowStatusModal(false);
              setCustomerToUpdate(null);
              setNewStatus(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant={newStatus ? "success" : "danger"}
            onClick={confirmToggleStatus}
            disabled={updatingStatus[customerToUpdate?.id]}
          >
            {updatingStatus[customerToUpdate?.id]
              ? "Updating..."
              : newStatus
                ? "Activate"
                : "Deactivate"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CustomersList;
